import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { supabase } from "@/lib/supabase";
import { CampaignLaunchForm } from "@/features/marketing/CampaignsPanel";

// ============================================================================
// EmailAudienceModal — email a marketing template to a WHOLE AUDIENCE the user
// just built on a report (the All Customers list, or any future customer-list
// report). The caller hands us a label + a resolver that returns the emailable
// recipients (custno + email); we resolve them, pick a template, and hand the
// list to a CAMPAIGN (MKT-REDESIGN-01 M1, 2026-09-03): a record with a
// schedule, consent checked at queue time and again at send time, drained by
// the comms-run job at 100 every 10 minutes, cancellable from Marketing.
// The old in-browser send loop is gone.
// ============================================================================

export interface AudienceRecipient {
  custno: string;
  email: string;
  name?: string | null;
}

const CAP = 5000; // per campaign; the outbox paces it, the browser no longer does the work

export function EmailAudienceModal({
  audienceLabel,
  audienceSpec,
  resolveRecipients,
  onClose,
}: {
  audienceLabel: string;
  /** How the list was built (saved with the campaign so it can be rerun in M2). */
  audienceSpec?: Record<string, unknown>;
  resolveRecipients: () => Promise<AudienceRecipient[]>;
  onClose: () => void;
}) {
  const [recipients, setRecipients] = useState<AudienceRecipient[] | null>(null);
  const [templates, setTemplates] = useState<{ key: string; name: string }[]>([]);
  const [tpl, setTpl] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([
      resolveRecipients(),
      supabase.from("pegasus_template").select("key,name").eq("kind", "marketing").eq("archived", false).order("name"),
    ]).then(([rec, tq]) => {
      if (!alive) return;
      setRecipients(rec);
      const t = (tq.data ?? []) as { key: string; name: string }[];
      setTemplates(t);
      if (t[0]) setTpl(t[0].key);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = recipients?.length ?? 0;
  const tplName = templates.find((t) => t.key === tpl)?.name ?? "";

  return (
    <Modal open onClose={onClose} title="Email These Customers" subtitle={audienceLabel} size="md">
      <div className="space-y-4">
        {recipients === null ? (
          <div className="py-6 text-center text-sm text-slate-400">Counting your audience…</div>
        ) : (
          <>
            <div className="rounded-lg border border-hairline bg-slate-50/60 px-3 py-2.5 text-sm text-slate-700">
              <span className="font-semibold text-brand-700">{total.toLocaleString()}</span> customer{total === 1 ? "" : "s"} with an email on file. This becomes a campaign — it shows in Marketing → Campaigns with its progress, and you can cancel it there.
            </div>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Which email</span>
              <select value={tpl} onChange={(e) => setTpl(e.target.value)} className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700">
                {templates.length === 0 && <option value="">Loading…</option>}
                {templates.map((t) => (
                  <option key={t.key} value={t.key}>{t.name}</option>
                ))}
              </select>
            </label>

            {tpl && (
              <CampaignLaunchForm
                defaultName={`${tplName} — ${audienceLabel} — ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                templateKey={tpl}
                templateName={tplName}
                audience={audienceSpec ?? { kind: "report", label: audienceLabel }}
                audienceLabel={audienceLabel}
                recipients={recipients.slice(0, CAP).map((r) => ({ custno: r.custno, to: r.email, name: r.name ?? null }))}
                excludedNote={total > CAP ? `Capped at ${CAP.toLocaleString()} per campaign.` : null}
              />
            )}

            <div className="flex items-center justify-end border-t border-hairline pt-3">
              <button onClick={onClose} className="rounded-lg border border-hairline px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Close</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
